import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Check, X as XIcon, Zap, Crown, Shield, Star, CreditCard, Plus,
  Trash2, History, AlertTriangle, RefreshCw, Loader2, ArrowRight,
  Calendar, MessageSquare, BookOpen, Sparkles, FileText, Trophy, Lock, Tag
} from 'lucide-react';

import API from '../utils/api';
import { useSyncRefresh } from '../hooks/useSyncRefresh';

var PLAN_STYLES = {
  free:     { accent: '#6B6690', border: 'rgba(107,102,144,0.28)', cls: 'plan-card-free',     icon: Shield },
  basic:    { accent: '#7C7DB8', border: 'rgba(124,125,184,0.32)', cls: 'plan-card-basic',    icon: Zap },
  standard: { accent: '#5A4E9E', border: 'rgba(90,78,158,0.40)',   cls: 'plan-card-standard', icon: Crown, popular: true },
  pro:      { accent: '#2D2560', border: 'rgba(45,37,96,0.45)',    cls: 'plan-card-pro',      icon: Star,  bestValue: true },
};

var TIER_ORDER = ['free', 'basic', 'standard', 'pro'];

var DEFAULT_BUNDLES = { free: 150, basic: 150, standard: 150, pro: 100 };

function buildPlanBullets(b) {
  return {
    free: ['Full calendar — free for 14 days', 'One free plan scan/mo', '3 journal entries/mo', 'Each course series: $' + b.free + ' · All-3 bundle: $300'],
    basic: ['All features unlocked', 'Co-parent messaging — free', '2 plan builder uses/mo', 'Each course series: $' + b.basic + ' · All-3 bundle: $300', 'Co-parent linking'],
    standard: ['All features unlocked', 'Co-parent messaging — free', '5 plan builders + scans/mo', 'Each course series: $' + b.standard + ' · All-3 bundle: $300', '200 video-call min/mo · unlimited calls', 'Compliance tracking'],
    pro: ['Everything unlimited', 'Co-parent messaging — free', 'Full AI avatar companion', 'Each course series: $' + b.pro + ' · All-3 bundle: $225', '450 video-call min/mo · unlimited calls', 'Priority support'],
  };
}

function buildFeaturePricing(b) {
  return { cat: 'Pricing', items: [
    { label: 'Each Mastery Series', free: '$' + b.free, basic: '$' + b.basic, standard: '$' + b.standard, pro: '$' + b.pro },
    { label: 'Beginner Course', free: 'Free', basic: 'Free', standard: 'Free', pro: 'Free' },
    { label: 'Intermediate Course', free: '$50', basic: '$50', standard: '$50', pro: '$30' },
    { label: 'Expert Course', free: '$50', basic: '$50', standard: '$50', pro: '$30' },
    { label: 'Master Course', free: '$50', basic: '$50', standard: '$50', pro: '$40' },
    { label: 'All-3 Series Bundle', free: '$300', basic: '$300', standard: '$300', pro: '$225' },
  ]};
}

var STATIC_FEATURES = [
  { cat: 'AI Features', items: [
    { label: 'AI Communication Analysis', free: false, basic: true, standard: true, pro: true },
    { label: 'AI Companion', free: false, basic: true, standard: true, pro: true },
    { label: 'Full Avatar Companion', free: false, basic: false, standard: false, pro: true },
    { label: 'AI Message Rewrites', free: false, basic: true, standard: true, pro: true },
    { label: 'Unlimited AI', free: false, basic: false, standard: false, pro: true },
  ]},
  { cat: 'Communication', items: [
    { label: 'Comms Check', free: false, basic: true, standard: true, pro: true },
    { label: 'Co-Parent Messaging', free: false, basic: true, standard: true, pro: true },
    { label: 'Change Requests', free: false, basic: true, standard: true, pro: true },
    { label: 'Co-Parent Linking', free: false, basic: true, standard: true, pro: true },
  ]},
  { cat: 'Planning & Analysis', items: [
    { label: 'Plan Scans / Month', free: '1', basic: '5', standard: '5 (+$5 each)', pro: 'Unlimited' },
    { label: 'Plan Builder / Month', free: '-', basic: '2', standard: '5 (+$5 each)', pro: 'Unlimited' },
    { label: 'Clause Library', free: false, basic: true, standard: true, pro: true },
  ]},
  { cat: 'Journal & Calendar', items: [
    { label: 'Journal Entries / Month', free: '3', basic: 'Unlimited', standard: 'Unlimited', pro: 'Unlimited' },
    { label: 'Calendar View', free: true, basic: true, standard: true, pro: true },
    { label: 'Calendar Events', free: '14-day trial', basic: true, standard: true, pro: true },
  ]},
  { cat: 'Video Calling', items: [
    { label: 'Monthly Minute Pool', free: '-', basic: '-', standard: '200 min', pro: '450 min' },
    { label: 'Calls per Month', free: '-', basic: '-', standard: 'Unlimited', pro: 'Unlimited' },
    { label: 'Recording & Transcription', free: false, basic: false, standard: true, pro: true },
    { label: 'Virtual Backgrounds', free: false, basic: false, standard: true, pro: true },
    { label: 'Recording Retention', free: '-', basic: '-', standard: '90 days', pro: 'Until you delete' },
  ]},
  { cat: 'Progress & Education', items: [
    { label: 'Course Access', free: true, basic: true, standard: true, pro: true },
    { label: 'Compliance Tracking', free: false, basic: true, standard: true, pro: true },
    { label: 'Case Files / Document Vault', free: false, basic: true, standard: true, pro: true },
    { label: 'Data Export', free: false, basic: true, standard: true, pro: true },
  ]},
  { cat: 'Support', items: [
    { label: 'Priority Support', free: false, basic: false, standard: false, pro: true },
  ]},
];

function FeatureCell({ val }) {
  if (val === true)  return <Check className="w-4 h-4 text-[#2D2560] mx-auto" strokeWidth={2.5} />;
  if (val === false) return <XIcon className="w-3.5 h-3.5 text-[#A8AAD4] mx-auto" />;
  return <span className="text-xs font-medium text-[#3A3566]">{val}</span>;
}

function PaymentMethodCard({ method, isDefault, onSetDefault, onRemove, loading }) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border transition-all"
      style={{ borderColor: isDefault ? 'rgba(217,70,239,0.35)' : 'rgba(255,255,255,0.06)', background: isDefault ? 'rgba(217,70,239,0.04)' : 'rgba(255,255,255,0.02)' }}>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white/[0.06]">
        <CreditCard className="w-5 h-5 text-white/60" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-mono text-white/80">**** {method.last4}</span>
          <span className="text-xs font-bold uppercase text-white/40">{method.brand}</span>
          {isDefault && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#10B981]/15 text-[#10B981]">Default</span>}
        </div>
        <p className="text-xs text-white/35">Expires {String(method.exp_month).padStart(2, '0')}/{method.exp_year}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!isDefault && <button onClick={function () { onSetDefault(method.id); }} disabled={loading} className="px-3 py-1.5 text-xs rounded-lg text-white font-medium hover:opacity-90 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)' }}>Set Default</button>}
        <button onClick={function () { onRemove(method.id); }} disabled={loading} className="p-2 rounded-lg text-white/30 hover:text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

var Billing = function () {
  var { user, refreshUser } = useAuth();
  var [tab, setTab] = useState('plans');
  var [interval, setInterval] = useState('yearly');
  var [loading, setLoading] = useState(null);
  var [subscription, setSubscription] = useState(null);
  var [paymentMethods, setPaymentMethods] = useState([]);
  var [defaultMethod, setDefaultMethod] = useState(null);
  var [transactions, setTransactions] = useState([]);
  var [fetching, setFetching] = useState(false);
  var [actionLoading, setActionLoading] = useState(false);
  var [cancelConfirm, setCancelConfirm] = useState(false);
  var [courseBundles, setCourseBundles] = useState(DEFAULT_BUNDLES);

  var PLAN_BULLETS = buildPlanBullets(courseBundles);
  var FEATURES = [buildFeaturePricing(courseBundles)].concat(STATIC_FEATURES);

  var currentPlan = (user && user.subscription_plan) || 'free';
  var currentInterval = (user && user.billing_interval) || 'monthly';

  var loadBillingData = useCallback(function () {
    setFetching(true);
    Promise.all([
      axios.get(API + '/billing/subscription', { withCredentials: true }).catch(function () { return { data: {} }; }),
      axios.get(API + '/billing/payment-methods', { withCredentials: true }).catch(function () { return { data: { methods: [] } }; }),
      axios.get(API + '/billing/payment-history', { withCredentials: true }).catch(function () { return { data: { transactions: [] } }; }),
      axios.get(API + '/courses/pricing', { withCredentials: true }).catch(function () { return { data: {} }; }),
    ]).then(function (res) {
      setSubscription(res[0].data);
      setPaymentMethods(res[1].data.methods || []);
      setDefaultMethod(res[1].data.default_method);
      setTransactions(res[2].data.transactions || []);
      var tb = res[3].data?.series_pricing?.[0]?.bundle_pricing?.tier_bundles;
      if (tb) setCourseBundles(tb);
    }).finally(function () { setFetching(false); });
  }, []);

  useEffect(function () { loadBillingData(); }, [loadBillingData]);
  useSyncRefresh(loadBillingData);

  var handleDowngrade = async function (planId) {
    if (!window.confirm('Are you sure you want to downgrade to the ' + planId.charAt(0).toUpperCase() + planId.slice(1) + ' plan? Changes take effect at end of your current billing period.')) return;
    setLoading(planId);
    try {
      await axios.post(API + '/billing/downgrade', { plan: planId }, { withCredentials: true });
      loadBillingData();
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Downgrade failed');
    }
    setLoading(null);
  };

  var handleUpgrade = async function (planId) {
    if (planId === 'free') return;
    setLoading(planId);
    try {
      var payload = { plan: planId, interval: interval, trial: true, origin_url: window.location.origin };
      var res = await axios.post(API + '/billing/create-checkout', payload, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      alert('Failed to create checkout session.');
      setLoading(null);
    }
  };

  var handleAddPayment = async function () {
    setActionLoading(true);
    try {
      var res = await axios.post(API + '/billing/add-payment-method', { origin_url: window.location.origin }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) { alert('Failed to start setup.'); }
    finally { setActionLoading(false); }
  };

  var handleSetDefault = async function (pmId) {
    setActionLoading(true);
    try { await axios.post(API + '/billing/set-default-payment-method', { payment_method_id: pmId }, { withCredentials: true }); loadBillingData(); }
    catch (e) { alert('Failed.'); }
    finally { setActionLoading(false); }
  };

  var handleRemove = async function (pmId) {
    if (!window.confirm('Remove this payment method?')) return;
    setActionLoading(true);
    try { await axios.delete(API + '/billing/payment-method/' + pmId, { withCredentials: true }); loadBillingData(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed.'); }
    finally { setActionLoading(false); }
  };

  var handleCancel = async function () {
    setActionLoading(true);
    try { await axios.post(API + '/billing/cancel-subscription', {}, { withCredentials: true }); setCancelConfirm(false); loadBillingData(); if (refreshUser) refreshUser(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed.'); }
    finally { setActionLoading(false); }
  };

  var sub = subscription?.subscription;
  var isCanceling = sub?.cancel_at_period_end;
  var periodEnd = sub?.current_period_end ? new Date(sub.current_period_end * 1000) : null;

  var tabs = [
    { id: 'plans', label: 'Plans' },
    { id: 'compare', label: 'Compare Features' },
    { id: 'methods', label: 'Payment Methods' },
    { id: 'history', label: 'History' },
  ];

  return (
    <div className="space-y-5" data-testid="billing-page">
      {/* Header */}
      <div className="card-surface p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold mb-0.5">Billing & Subscription</h2>
            <p className="text-white/50 text-sm">
              Current: <span className="font-semibold text-white capitalize">{currentPlan}</span>
              <span className="text-white/30 ml-1">({currentInterval})</span>
              {currentPlan !== 'free' && sub && (
                <span className="ml-2 text-xs text-white/30">
                  {isCanceling ? '(Cancels ' + (periodEnd ? periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '') + ')' : sub.status === 'active' ? '(Renews ' + (periodEnd ? periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '') + ')' : ''}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {currentPlan !== 'free' && !isCanceling && (
              <button onClick={function () { setCancelConfirm(true); }} className="px-3 py-1.5 text-xs rounded-lg text-white font-medium hover:opacity-90" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)' }} data-testid="cancel-subscription-btn">Cancel</button>
            )}
            {isCanceling && (
              <button onClick={function () { axios.post(API + '/billing/reactivate-subscription', {}, { withCredentials: true }).then(function () { loadBillingData(); }); }}
                className="px-3 py-1.5 text-xs rounded-lg text-white font-medium hover:opacity-90" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)' }} data-testid="reactivate-subscription-btn">Reactivate</button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/[0.06] rounded-xl">
        {tabs.map(function (t) {
          return <button key={t.id} onClick={function () { setTab(t.id); }}
            className={'flex-1 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ' + (tab === t.id ? 'bg-gradient-to-r from-[#2D2560]/20 to-[#5A4E9E]/20 text-white border border-[#2D2560]/30' : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]')}
            data-testid={'billing-tab-' + t.id}>{t.label}</button>;
        })}
      </div>

      {/* ===== PLANS TAB ===== */}
      {tab === 'plans' && (
        <div className="space-y-4">
          {/* Interval Toggle */}
          <div className="flex items-center justify-center gap-3" data-testid="interval-toggle">
            <span className={'text-sm font-medium ' + (interval === 'monthly' ? 'text-white' : 'text-white/40')}>Monthly</span>
            <button onClick={function () { setInterval(interval === 'monthly' ? 'yearly' : 'monthly'); }}
              className="relative w-14 h-7 rounded-full transition-colors" style={{ background: interval === 'yearly' ? 'linear-gradient(135deg, #2D2560, #5A4E9E)' : 'rgba(255,255,255,0.1)' }}
              data-testid="billing-interval-toggle">
              <div className={'absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ' + (interval === 'yearly' ? 'translate-x-8' : 'translate-x-1')}></div>
            </button>
            <span className={'text-sm font-medium ' + (interval === 'yearly' ? 'text-white' : 'text-white/40')}>Yearly</span>
            {interval === 'yearly' && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-white text-[#2D2560] border border-[#2D2560]/30 shadow-sm">Save up to $160</span>}
          </div>

          {/* Plan Cards — matches Landing page style */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-stretch">
            {TIER_ORDER.map(function (tier) {
              var s = PLAN_STYLES[tier];
              var cfg = { free: { monthly: 0, yearly: 0, yearly_savings: 0, name: 'Free' }, basic: { monthly: 9.99, yearly: 99.90, yearly_savings: 19.98, name: 'Basic' }, standard: { monthly: 24.99, yearly: 249.90, yearly_savings: 49.98, name: 'Standard' }, pro: { monthly: 34.99, yearly: 349.90, yearly_savings: 69.98, name: 'Pro' } }[tier];
              var price = interval === 'yearly' ? cfg.yearly : cfg.monthly;
              var isCurrent = currentPlan === tier;
              var isDowngrade = TIER_ORDER.indexOf(currentPlan) > TIER_ORDER.indexOf(tier);
              var ctaDisabled = loading === tier || isCurrent || tier === 'free';
              var ctaStyle = { background: 'linear-gradient(135deg, #2D2560, #3A3178, #5A4E9E)', color: '#ffffff', cursor: isCurrent || tier === 'free' ? 'default' : 'pointer' };

              // Premium glows — Standard moderate, Pro deeper. Matches Landing page.
              var glowStyle = {};
              if (s.bestValue) {
                glowStyle = { boxShadow: '0 0 0 1px rgba(45,37,96,0.45), 0 14px 38px -10px rgba(45,37,96,0.55), 0 0 22px rgba(45,37,96,0.32)' };
              } else if (s.popular) {
                glowStyle = { boxShadow: '0 0 0 1px rgba(90,78,158,0.35), 0 10px 28px -10px rgba(90,78,158,0.45), 0 0 16px rgba(90,78,158,0.22)' };
              }
              var ringCls = s.bestValue
                ? ' ring-2 ring-[#2D2560]/55'
                : s.popular ? ' ring-2 ring-[#5A4E9E]/45' : '';

              // On yearly, video-call minutes are unlocked upfront for the whole year.
              var bullets = PLAN_BULLETS[tier];
              if (interval === 'yearly' && (tier === 'standard' || tier === 'pro')) {
                var monthlyMin = tier === 'standard' ? 200 : 450;
                var yearlyMin = monthlyMin * 12;
                bullets = bullets.map(function (f) {
                  if (/video-call/i.test(f)) {
                    return yearlyMin.toLocaleString() + ' video-call min upfront for the year · unlimited calls';
                  }
                  return f;
                });
              }

              return (
                <div key={tier} className={'card-surface plan-card p-5 sm:p-6 flex flex-col h-full ' + s.cls + ringCls}
                  style={glowStyle} data-testid={'plan-card-' + tier}>
                  {/* Reserve a fixed badge slot on every card so heading rows align. */}
                  <div className="h-6 mb-3">
                    {s.popular && !s.bestValue && (
                      <span className="inline-block px-2.5 py-0.5 bg-[#5A4E9E] text-white text-[10px] font-bold uppercase tracking-wider rounded-full" data-testid="badge-most-popular">Most Popular</span>
                    )}
                    {s.bestValue && (
                      <span className="inline-block px-2.5 py-0.5 bg-[#2D2560] text-white text-[10px] font-bold uppercase tracking-wider rounded-full" data-testid="badge-best-value">Best Value</span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold mb-0.5" style={{ color: s.accent }}>{cfg.name}</h3>
                  <div className="mb-4 min-h-[56px]">
                    {price === 0
                      ? <span className="text-3xl font-extrabold text-[#1E1A3E]">Free</span>
                      : <><span className="text-3xl font-extrabold text-[#1E1A3E]">${price}</span><span className="text-[#6B6690] text-xs">/{interval === 'yearly' ? 'yr' : 'mo'}</span></>
                    }
                    {interval === 'yearly' && cfg.yearly_savings > 0 && (
                      <p className="text-[11px] text-[#2D2560] mt-1 font-medium">Save ${cfg.yearly_savings}/year</p>
                    )}
                  </div>
                  <ul className="space-y-2 mb-5 text-left flex-1">
                    {bullets.map(function (feat, fi) {
                      return (
                        <li key={fi} className="flex items-start gap-2 text-[15px] leading-[20px] text-[#1A1738]">
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#2D2560]" strokeWidth={2.5} />
                          <span>{feat}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <button onClick={function () { isDowngrade ? handleDowngrade(tier) : handleUpgrade(tier); }}
                    disabled={ctaDisabled}
                    className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-95 mt-auto"
                    style={ctaStyle}
                    data-testid={(isDowngrade ? 'downgrade-' : 'upgrade-') + tier + '-btn'}>
                    {loading === tier ? 'Loading...' : isCurrent ? 'Current Plan' : isDowngrade ? 'Downgrade' : tier === 'free' ? 'Free Tier' : 'Start 7-Day Trial'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Proration Notice */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
            <p className="text-xs text-white/50">Upgrades are prorated. You will only be charged the difference based on your remaining billing period. All paid plans include 7-day free trial.</p>
          </div>
        </div>
      )}

      {/* ===== COMPARE FEATURES TAB ===== */}
      {tab === 'compare' && (
        <div className="card-surface overflow-hidden" data-testid="feature-comparison">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-[#F6F7FC]">
                <tr>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#6B6690] p-3 w-[200px]">Feature</th>
                  {TIER_ORDER.map(function (tier) {
                    var s = PLAN_STYLES[tier];
                    var isCurrent = currentPlan === tier;
                    var cfg = { free: { m: 0, y: 0, s: 0 }, basic: { m: 15, y: 150, s: 30 }, standard: { m: 35, y: 350, s: 70 }, pro: { m: 65, y: 650, s: 130 } }[tier];
                    var price = interval === 'yearly' ? cfg.y : cfg.m;
                    return (
                      <th key={tier} className="p-3 text-center min-w-[120px]" style={isCurrent ? { borderTop: '3px solid ' + s.accent, background: '#EEEFF8' } : {}}>
                        <p className="text-sm font-bold capitalize" style={{ color: s.accent }}>{tier}</p>
                        <p className="text-lg font-extrabold text-[#1E1A3E]">{price === 0 ? 'Free' : '$' + price}</p>
                        <p className="text-xs text-[#6B6690]">{price > 0 ? '/' + (interval === 'yearly' ? 'yr' : 'mo') : ''}</p>
                        {interval === 'yearly' && cfg.s > 0 && <p className="text-xs font-medium text-[#2D2560]">Save ${cfg.s}</p>}
                        {isCurrent && <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-[#2D2560] text-white">Current</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map(function (group) {
                  return (
                    <React.Fragment key={group.cat}>
                      <tr className="bg-[#F6F7FC]/60"><td colSpan={5} className="px-3 pt-4 pb-1"><p className="text-xs font-bold uppercase tracking-wider text-[#2D2560]">{group.cat}</p></td></tr>
                      {group.items.map(function (item, idx) {
                        return (
                          <tr key={idx} className="border-t border-[#DDE1EE]/60 hover:bg-[#F6F7FC]/50 transition-colors">
                            <td className="px-3 py-2.5 text-xs text-[#3A3566]">{item.label}</td>
                            {TIER_ORDER.map(function (tier) {
                              var isCurrent = currentPlan === tier;
                              return (
                                <td key={tier} className="px-3 py-2.5 text-center" style={isCurrent ? { background: 'rgba(90,78,158,0.06)' } : {}}>
                                  <FeatureCell val={item[tier]} />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-[#DDE1EE] bg-[#F6F7FC]/60 text-center">
            <p className="text-xs text-[#6B6690]">Upgrades are prorated. You will only be charged the difference based on your remaining billing period.</p>
          </div>
        </div>
      )}

      {/* ===== PAYMENT METHODS TAB ===== */}
      {tab === 'methods' && (
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)' }}><CreditCard className="w-4 h-4 text-white" /></div>
              <div>
                <h3 className="text-sm font-semibold">Payment Methods</h3>
                <p className="text-xs text-white/35">{paymentMethods.length === 0 ? 'No cards on file' : paymentMethods.length + ' card' + (paymentMethods.length > 1 ? 's' : '') + ' on file'}</p>
              </div>
            </div>
            <button onClick={handleAddPayment} disabled={actionLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)', color: 'white' }} data-testid="add-payment-method-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Card
            </button>
          </div>
          {paymentMethods.length === 0 ? (
            <div className="py-8 text-center" data-testid="no-payment-methods"><CreditCard className="w-8 h-8 text-white/10 mx-auto mb-2" /><p className="text-white/40 text-sm">No payment methods on file</p></div>
          ) : (
            <div className="space-y-3" data-testid="payment-methods-list">{paymentMethods.map(function (m) { return <PaymentMethodCard key={m.id} method={m} isDefault={m.is_default} onSetDefault={handleSetDefault} onRemove={handleRemove} loading={actionLoading} />; })}</div>
          )}
        </div>
      )}

      {/* ===== HISTORY TAB ===== */}
      {tab === 'history' && (
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #5A4E9E, #3B82F6)' }}><History className="w-4 h-4 text-white" /></div>
              <h3 className="text-sm font-semibold">Payment History</h3>
            </div>
            <button onClick={loadBillingData} disabled={fetching} className="p-2 text-white/30 hover:text-white hover:bg-white/5 rounded-lg" data-testid="refresh-history-btn">
              <RefreshCw className={'w-4 h-4 ' + (fetching ? 'animate-spin' : '')} />
            </button>
          </div>
          {transactions.length === 0 ? (
            <div className="py-8 text-center" data-testid="no-transactions"><History className="w-8 h-8 text-white/10 mx-auto mb-2" /><p className="text-white/40 text-sm">No transactions yet</p></div>
          ) : (
            <div data-testid="transaction-list">
              {transactions.map(function (tx) {
                var sc = { paid: '#10B981', pending: '#EAB308', failed: '#EF4444' }[tx.payment_status] || '#EAB308';
                var d = tx.created_at ? new Date(tx.created_at) : null;
                return (
                  <div key={tx.session_id} className="flex items-center gap-4 py-3 border-b border-white/[0.04] last:border-b-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium capitalize">{tx.plan} Plan {tx.interval ? '(' + tx.interval + ')' : ''}</p>
                      <p className="text-xs text-white/30">{d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
                    </div>
                    <span className="text-sm font-semibold">${tx.amount?.toFixed(2)}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase" style={{ background: sc + '15', color: sc }}>{tx.payment_status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Cancel Modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="cancel-modal">
          <div className="bg-[#1a1c2e] border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#EF4444]/15 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-[#EF4444]" /></div>
              <div><h3 className="font-semibold">Cancel Subscription?</h3><p className="text-xs text-white/40">Your plan will remain active until the end of the billing period</p></div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={function () { setCancelConfirm(false); }} className="flex-1 py-2.5 rounded-lg text-sm text-white font-medium hover:opacity-90" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)', opacity: 0.7 }} data-testid="cancel-modal-dismiss">Keep Plan</button>
              <button onClick={handleCancel} disabled={actionLoading} className="flex-1 py-2.5 rounded-lg text-sm text-white font-medium hover:opacity-90 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #2D2560, #5A4E9E)' }} data-testid="cancel-modal-confirm">{actionLoading ? 'Cancelling...' : 'Yes, Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
